<script lang="ts" setup>
/// <reference types="vite-svg-loader" />
import VSpinner from '@shared/component/VSpinner/VSpinner.vue';
import { LicensePlate } from '@license-plate/licensePlate';
import { computed, ref, useTemplateRef, watch } from 'vue';

import StringField from '../StringField/StringField.vue';
import { type UpdateEventOf } from '../types';
import logo from './assets/kenteken_eu.svg?url';

type KentekenFieldSize = 'sm' | 'md';

const props = withDefaults(
  defineProps<{
    /** Wordt op de <input> gezet zodat een bijbehorend <label for="..."> het veld correct benoemt. */
    id?: string;
    path?: string;
    value?: string | null;
    name?: string;
    placeholder?: string;
    loading?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    autocomplete?: boolean;
    hideAdditionalData?: boolean;
    hideCountryBadge?: boolean;
    size?: KentekenFieldSize;
    additionalData?: {
      merk?: string;
      model?: string;
      bouwjaar?: string;
    };
  }>(),
  {
    disabled: false,
    readonly: false,
    loading: false,
    hideAdditionalData: false,
    hideCountryBadge: false,
    autocomplete: true,
    size: 'md',
  },
);

const emit = defineEmits<{
  updateField: [value: UpdateEventOf<string | null>];
  'update:value': [value?: string | null];
}>();

const sizeClasses: Record<
  KentekenFieldSize,
  { image: string; input: string; inputWithoutBadge: string }
> = {
  sm: {
    image: 'h-8',
    input: 'h-8 w-32 text-base',
    inputWithoutBadge: 'h-8 w-28 text-base',
  },
  md: {
    image: 'h-10',
    input: 'h-10 w-44 text-xl',
    inputWithoutBadge: 'h-10 w-36 text-xl',
  },
};

const addtionalDescription = computed(() => {
  if (!props.additionalData || !Object.keys(props.additionalData).length) return undefined;
  const { merk, model, bouwjaar } = props.additionalData;
  const bouwjaarString = bouwjaar ? `(${bouwjaar})` : '';
  const description = `${merk ?? ''} ${model ?? ''} ${bouwjaarString}`.trim();
  return description.length ? description : undefined;
});

const val = ref(props.value);
const inputRef = useTemplateRef('inputRef');

function focus() {
  inputRef.value?.focus();
}

defineExpose({ focus });

function onBlur(event: FocusEvent) {
  const target = event.target as HTMLInputElement;
  const plate = new LicensePlate(target.value);

  if (val.value === undefined) return;
  if (!plate.valid()) {
    emit('updateField', { path: props.path, value: val.value });
    emit('update:value', val.value);
  }
}

function onInput(event: Event) {
  const target = event.target as HTMLInputElement;
  if (!target.value) return;
  const plate = new LicensePlate(target.value);

  if (plate.valid()) {
    val.value = plate.pretty();
    emit('updateField', { path: props.path, value: val.value });
    emit('update:value', val.value);
  }
}

function onKeyDown(event: KeyboardEvent) {
  const regex = /^[a-zA-Z0-9-]+$/;
  const target = event.target as HTMLInputElement;
  if (!target.value) return;
  if (!regex.test(event.key) || (event.code === 'Minus' && target.value.slice(-1) === '-'))
    stopPropagation(event);
}

function stopPropagation(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}

watch(
  () => props.value,
  (newValue) => (val.value = newValue),
);
</script>

<template>
  <div class="flex w-full flex-col">
    <div
      class="relative flex"
      :class="[
        {
          'mb-2': !hideAdditionalData,
        },
      ]"
    >
      <img
        v-if="!hideCountryBadge"
        class="rounded-l-lg"
        :class="[sizeClasses[props.size].image, { grayscale: disabled }]"
        :src="logo"
        alt="Kenteken"
      />
      <input
        ref="inputRef"
        :id="id"
        class="min-w-0 border-none text-center font-['kenteken'] font-bold uppercase outline-none"
        :class="[
          hideCountryBadge ? 'rounded-lg' : 'rounded-l-none rounded-r-lg',
          hideCountryBadge
            ? sizeClasses[props.size].inputWithoutBadge
            : sizeClasses[props.size].input,
          {
            'bg-[#fcd754]': !disabled && !readonly,
            'bg-grey-200': disabled,
            'cursor-default bg-grey-200': readonly,
          },
        ]"
        type="text"
        v-model="val"
        :disabled="disabled || readonly"
        @blur="onBlur"
        @input="onInput"
        @keydown="onKeyDown"
        maxlength="8"
        data-testid="kenteken-field-input"
        spellcheck="false"
        :name="name"
        :autocomplete="autocomplete ? 'on' : 'off'"
      />
      <VSpinner v-if="loading" />
    </div>
    <StringField
      v-if="!hideAdditionalData"
      :value="addtionalDescription"
      disabled
      :readonly="readonly"
      class="additional-data-field"
      placeholder="Voertuiggegevens"
      data-testid="additional-data-field"
    />
  </div>
</template>
